import React from "react"
import get from "lodash.get"
import { getLabel } from "@/_services/lists"
import { getLocale } from "@/_services/localization"
import { formatDate, formatDateTime } from "@/_services/utils"
import { formatBytes, formatPercentage, removeAllAccents } from "basikon-common-utils"

export function getSearchParamName(searchFromUrl) {
  return typeof searchFromUrl === "string" ? searchFromUrl + "_search" : "search"
}

function match(str, search) {
  return removeAllAccents(str.toString()).toLowerCase().includes(removeAllAccents(search))
}

// __originalIndex contains the index of the row in the original array (the one passed via the "data" field).
// It is returned to the onRowChange, onRowDelete, and onRowDuplicate events via the row._dataIndex parameter.
// If the events manipulate the data, you must use row._dataIndex instead of row._index and rowIndex!
export function filterData(columns = [], data = [], search, options) {
  const { preserveOriginalIndex } = options || {}
  if (!search || typeof search !== "string") {
    return preserveOriginalIndex
      ? data.map((dataRow, dataIndex) => {
          if (dataRow) dataRow.__originalIndex = dataIndex
          return dataRow
        })
      : data
  }

  search = search.trim().toLowerCase()
  const locale = getLocale()

  const names = []
  const columnsMap = {}
  for (const column of columns) {
    const name = column?.name || column?.formInputProps?.field
    names.push(name)
    columnsMap[name] = column
  }

  const returnedData = []
  for (let i = 0; i < data?.length; i++) {
    const row = data[i]
    if (preserveOriginalIndex && row) row.__originalIndex = i
    if (row.content) {
      // Ignore nested rows when filtering
      returnedData.push(row)
      continue
    }

    for (const name of names) {
      let component = get(row, name)

      // Number
      if (component && !isNaN(component)) component = component.toString()

      // Text
      if (typeof component === "string") {
        // Match the label, not the value, especially if the "string" is from a column with a "select" prop.
        const select = columnsMap[name]?.select || columnsMap[name]?.formInputProps?.select
        if (select) {
          const label = getLabel(select, component)
          if (label && match(label, search)) {
            returnedData.push(row)
            break
          }
        } else if (match(component, search)) {
          returnedData.push(row)
          break
        }
      }
      // Array of strings?
      else if (Array.isArray(component) && component.length) {
        const select = columnsMap[name]?.select || columnsMap[name]?.formInputProps?.select
        let skip = false
        for (const value of component) {
          if (select) {
            const label = getLabel(select, value)
            if (label && match(label, search)) {
              returnedData.push(row)
              skip = true
              break
            }
          }
        }
        if (skip) break
      }

      // <Component/>
      if (React.isValidElement(component)) {
        const { registration, type, select, children, obj = {}, field } = component.props // <FormInput/> props
        let { value } = component.props

        if (!value && field) value = obj[field]
        if (!value && registration) value = registration // <PersonLink/>

        if (typeof children === "string" && match(children, search)) {
          returnedData.push(row)
          break
        } else if (Array.isArray(children)) {
          let skip = false
          for (const child of children) {
            if (!child) continue
            if (typeof child === "string" && match(child, search)) {
              returnedData.push(row)
              skip = true
              break
            }
            if (typeof child.props?.children === "string" && match(child.props.children, search)) {
              returnedData.push(row)
              skip = true
              break
            }
          }
          if (skip) break
        }

        if (value) {
          if (type === "date") value = formatDate(value, locale)
          if (type === "datetime") value = formatDateTime(value, locale)
          if (type === "percentage") value = formatPercentage(value, locale)
          if (type === "bytes") value = formatBytes(value)
          if (type === "object" && (typeof value === "object" || Array.isArray(value))) value = JSON.stringify(value)
          if (select) {
            if (typeof select === "string") value = getLabel(select, value)
            if (Array.isArray(select)) {
              const option = select.find(o => o.value === value) || {}
              value = option.label || option.value || value
            }
          }

          if (match(value, search)) {
            returnedData.push(row)
            break
          }
        }
      }
    }
  }
  return returnedData
}
